import { useState, useRef, useCallback, useEffect } from "react";
import ActionBar, { type ActionBarMode } from "./UI/ActionBar";
import Avatar from "./UI/Avatar";
import { useAuth } from "@modules/auth/hooks/useAuth";
import Button from "./UI/Button";
import Input from "./UI/Input";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { TEditUser, type TEditUserSchema } from "@core/types/TEditUser";
import { useEditUser } from "@core/hooks";
import PhoneNumberInput from "./UI/AlgerianPhoneInput";
import { getDirtyPayload } from "@core/utils";

type ProfileSettingsProps = {
  onClose: VoidFunction;
};

function ProfileSettings({ onClose }: ProfileSettingsProps) {
  const [mode, setMode] = useState<ActionBarMode>("view");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();
  const editUserMutation = useEditUser();

  const {
    register,
    handleSubmit,
    formState: { isDirty, errors, dirtyFields },
    reset,
    control,
  } = useForm<TEditUserSchema>({
    resolver: zodResolver(TEditUser),
    defaultValues: {
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email: user?.email || "",
      phone_number: user?.phone_number || "",
    },
  });

  const handleFileSelect = useCallback((file: File) => {
    if (!file.type.startsWith("image/")) {
      alert("Please select an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert("File size must be less than 5MB");
      return;
    }

    setSelectedFile(file);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  }, []);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleChooseImage = () => {
    fileInputRef.current?.click();
  };

  const onSubmit = (data: TEditUserSchema) => {
    editUserMutation.mutate(getDirtyPayload(data, dirtyFields), {
      onSuccess: () => {
        setMode("view");
        reset(data);
      },
    });
  };

  const handleCancel = () => {
    reset();
    handleRemoveImage();
    setMode("view");
  };

  const handleModeChange = (newMode: ActionBarMode) => {
    if (newMode === "view" && mode === "edit") {
      handleCancel();
    } else {
      setMode(newMode);
    }
  };

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const currentImageUrl = previewUrl || user?.picture;
  const isEditing = mode === "edit";

  return (
    <ActionBar
      title="Profile Settings"
      editable
      mode={mode}
      onModeChange={handleModeChange}
      saveAs="button"
      minWidth="30rem"
      onSave={handleSubmit(onSubmit)}
      saveDisabled={!isDirty && !selectedFile}
      saveLoading={editUserMutation.isPending}
      onClose={onClose}
    >
      <form
        className="flex flex-col gap-4 p-2"
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="flex items-center gap-4">
          <Avatar size="lg" className="relative">
            {currentImageUrl ? (
              <img
                src={currentImageUrl}
                alt="User Avatar"
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <>
                {user?.first_name?.charAt(0)}
                {user?.last_name?.charAt(0)}
              </>
            )}
          </Avatar>

          <div className="flex flex-col gap-2">
            <h5 className="text-primary-950">
              {user?.last_name} {user?.first_name}
            </h5>

            {isEditing ? (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  type="button"
                  disabled
                  onClick={handleChooseImage}
                >
                  Choose image
                </Button>
                {(selectedFile || user?.picture) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={handleRemoveImage}
                    className="text-red-600 hover:text-red-700"
                  >
                    Remove
                  </Button>
                )}
              </div>
            ) : (
              <Button type="button" variant="secondary" size="sm" disabled>
                Choose image
              </Button>
            )}
          </div>
        </div>

        {isEditing && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            disabled
          />
        )}

        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-5">
            <div className="w-full">
              <Input
                className="w-full"
                label="First Name"
                {...register("first_name")}
                disabled={!isEditing}
                state={errors.first_name?.message ? "error" : "default"}
                helperText={errors.first_name?.message}
              />
            </div>
            <div className="w-full">
              <Input
                className="w-full"
                label="Last Name"
                {...register("last_name")}
                disabled={!isEditing}
                state={errors.last_name?.message ? "error" : "default"}
                helperText={errors.last_name?.message}
              />
            </div>
          </div>

          <Input
            className="w-full"
            label="Email"
            {...register("email")}
            disabled
            state={errors.email?.message ? "error" : "default"}
            helperText={errors.email?.message}
          />

          <Controller
            name="phone_number"
            control={control}
            render={({ field }) => (
              <PhoneNumberInput
                className="w-full pl-16"
                label="Phone Number"
                placeholder="xxx-xx-xx-xx"
                disabled={!isEditing}
                state={errors.phone_number?.message ? "error" : "default"}
                helperText={errors.phone_number?.message}
                value={field.value || ""} // full value: "+213XXXXXXXXX"
                onChange={field.onChange} // RHF will store full string
                onBlur={field.onBlur}
              />
            )}
          />
        </div>
      </form>
    </ActionBar>
  );
}

export default ProfileSettings;
